import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { theme, motion } from '../styles/index.js';

interface ToastAction {
  label: string;
  callback: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration: number;
  action?: ToastAction;
}

@customElement('toast-manager')
export class ToastManager extends LitElement {
  static styles = [
    theme,
    motion,
    css`
      :host {
        position: fixed;
        bottom: var(--space-lg);
        right: var(--space-lg);
        z-index: 9999;
        pointer-events: none;
      }

      .toast-container {
        display: flex;
        flex-direction: column-reverse;
        gap: var(--space-sm);
      }

      .toast {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        padding: var(--space-sm) var(--space-md);
        border-radius: var(--radius-sm);
        background: var(--bg-glass-strong);
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        border: 1px solid var(--border-glass);
        color: var(--t-primary);
        font-size: var(--text-md);
        pointer-events: auto;
        animation: motion-slide-up var(--motion-normal) var(--motion-ease-out) both;
        max-width: 350px;
        box-shadow: var(--shadow-lg);
      }

      .toast.exiting {
        animation: motion-slide-out-down var(--motion-normal) var(--motion-ease-in-out) both;
      }

      .icon {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
      }

      .toast.success .icon {
        color: var(--color-success);
      }
      .toast.error .icon {
        color: var(--color-error);
      }
      .toast.info .icon {
        color: var(--accent-secondary);
      }
      .toast.warning .icon {
        color: var(--color-warning);
      }

      .toast.success {
        border-left: 3px solid var(--color-success);
      }
      .toast.error {
        border-left: 3px solid var(--color-error);
      }
      .toast.info {
        border-left: 3px solid var(--accent-secondary);
      }
      .toast.warning {
        border-left: 3px solid var(--color-warning);
      }

      .message {
        flex: 1;
        line-height: 1.4;
      }

      .close {
        background: none;
        border: none;
        color: var(--t-muted);
        cursor: pointer;
        padding: 4px;
        border-radius: var(--radius-sm);
        transition: all 0.2s;
      }

      .close:hover {
        color: var(--t-primary);
        background: var(--bg-glass);
      }

      .toast-action {
        background: none;
        border: 1px solid var(--border-glass);
        color: var(--accent-secondary);
        cursor: pointer;
        padding: 3px 10px;
        border-radius: var(--radius-sm);
        font-size: var(--text-sm);
        font-weight: 500;
        transition: all 0.2s;
        white-space: nowrap;
        flex-shrink: 0;
      }

      .toast-action:hover {
        background: var(--bg-glass);
        border-color: var(--accent-secondary);
      }

      /* Progress countdown bar */
      .toast-progress {
        position: absolute;
        bottom: 0;
        left: 0;
        height: 2px;
        border-radius: 0 0 var(--radius-sm) var(--radius-sm);
        animation: toast-countdown linear forwards;
      }

      @keyframes toast-countdown {
        from {
          width: 100%;
        }
        to {
          width: 0%;
        }
      }

      .toast.success .toast-progress {
        background: var(--color-success);
      }
      .toast.error .toast-progress {
        background: var(--color-error);
      }
      .toast.info .toast-progress {
        background: var(--accent-secondary);
      }
      .toast.warning .toast-progress {
        background: var(--color-warning);
      }

      .toast {
        position: relative;
        overflow: hidden;
      }

      /* Pause countdown on hover */
      .toast:hover .toast-progress {
        animation-play-state: paused;
      }
    `,
  ];

  @state() private _toasts: Toast[] = [];
  private _exitingToasts = new Set<string>();

  static instance: ToastManager | null = null;

  connectedCallback() {
    super.connectedCallback();
    ToastManager.instance = this;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (ToastManager.instance === this) {
      ToastManager.instance = null;
    }
  }

  static show(
    message: string,
    type: Toast['type'] = 'info',
    duration = 3000,
    action?: ToastAction
  ) {
    if (ToastManager.instance) {
      ToastManager.instance.addToast(message, type, duration, action);
    }
  }

  addToast(message: string, type: Toast['type'] = 'info', duration = 3000, action?: ToastAction) {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const toast: Toast = { id, message, type, duration, action };

    this._toasts = [...this._toasts, toast];

    if (duration > 0) {
      setTimeout(() => this._dismissToast(id), duration);
    }
  }

  private _dismissToast(id: string) {
    this._exitingToasts.add(id);
    this.requestUpdate();

    setTimeout(() => {
      this._exitingToasts.delete(id);
      this._toasts = this._toasts.filter((t) => t.id !== id);
    }, 300);
  }

  render() {
    return html`
      <div class="toast-container" role="status" aria-live="polite" aria-atomic="false">
        ${this._toasts.map(
          (toast) => html`
            <div
              class="toast ${toast.type} ${this._exitingToasts.has(toast.id) ? 'exiting' : ''}"
              role="alert"
              aria-live="${toast.type === 'error' ? 'assertive' : 'polite'}"
            >
              ${this._renderIcon(toast.type)}
              <span class="message">${toast.message}</span>
              ${toast.action
                ? html`<button
                    class="toast-action"
                    @click=${() => {
                      toast.action!.callback();
                      this._dismissToast(toast.id);
                    }}
                  >
                    ${toast.action.label}
                  </button>`
                : ''}
              <button
                class="close"
                @click=${() => this._dismissToast(toast.id)}
                aria-label="Dismiss notification"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
              ${toast.duration > 0
                ? html`<div
                    class="toast-progress"
                    style="animation-duration:${toast.duration}ms"
                  ></div>`
                : ''}
            </div>
          `
        )}
      </div>
    `;
  }

  private _renderIcon(type: Toast['type']) {
    const icons: Record<Toast['type'], string> = {
      success: '<path d="M20 6L9 17l-5-5"/>',
      error: '<circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/>',
      info: '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>',
      warning:
        '<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01"/>',
    };

    return html`
      <svg
        class="icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        ${this._unsafeSvg(icons[type])}
      </svg>
    `;
  }

  private _unsafeSvg(svg: string) {
    const template = document.createElement('template');
    template.innerHTML = svg;
    return html`${template.content}`;
  }
}

export function showToast(
  message: string,
  type: Toast['type'] = 'info',
  duration = 3000,
  action?: { label: string; callback: () => void }
) {
  ToastManager.show(message, type, duration, action);
}
